import { useContext, useMemo, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { ExpressRevealContext } from '@/context/ExpressRevealContext';
import { PaymentMethodEnum } from '@/types/common';
import { PollingStatus } from '@/types/order';
import { FeatureFlag } from '@/constants/featureFlags';
import { Modal } from '@/components/Common/Modal';
import { Divider } from '@/components/Common/Divider';
import { Error } from '@/components/Common/Error';
import { Footer } from '@/components/Footer/Footer';
import { Card3DSForm } from '@/components/Card3DSForm/Card3DSForm';
import { ProcessingLoader } from '@/components/ProcessingLoader/ProcessingLoader';
import { RedirectPayment } from '@/components/RedirectPayment/RedirectPayment';
import { SuccessScreen } from '@/components/SuccessScreen/SuccessScreen';
import { Payment } from '@/components/Payment/Payment';
import { TotalSummaryCard } from '@/components/TotalSummaryCard/TotalSummaryCard';
import { UseOtherPaymentMethodLink } from '@/components/UseOtherPaymentMethodLink/UseOtherPaymentMethodLink';
import { EmbeddedPreferredSkeletonContent } from '@/components/CheckoutSkeletonLoader/EmbeddedPreferredSkeleton';
import { useCheckoutDimensionsReporter } from '@/hooks/useCheckoutDimensionsReporter';
import { useExpressWalletFallback } from '@/hooks/useExpressWalletFallback';
import { useOtherPaymentMethodFallback } from '@/hooks/useOtherPaymentMethodFallback';
import {
  EXPRESS_WALLET_GATE_HANG_TIMEOUT_MS,
  EXPRESS_WALLET_GATE_HANG_TIMEOUT_REASON,
  useExpressReadyTimeout,
} from '@/hooks/useExpressReadyTimeout';
import { CHECKOUT_CONTENT_END_ATTRIBUTE } from '@/utils/checkoutDimensions';
import { logError } from '@/utils/logger';
import { cn } from '@/utils/cn';
import { TEMPLATE_CLASSES } from '@/constants/styles';
import type { TemplateViewProps } from '@/templates/CheckoutTemplate';

/**
 * Embedded shell + preferred single CTA.
 *
 *   ┌────────────────────────────────┐
 *   │  Total                 $19.90 ⌃│  TotalSummaryCard
 *   ├────────────────────────────────┤
 *   │  primary CTA                   │  <Payment /> (express-first)
 *   │  Use other payment method      │
 *   │  Footer                        │
 *   └────────────────────────────────┘
 *
 * No publisher hero and no bottom sheet: this checkout is a content-sized
 * iframe. Sub-screens render in place (`inlineSubScreens`). Apple Pay stays
 * under the skeleton until the PSP confirms the wallet; if it never does,
 * the template falls back to EMBEDDED_VIEW.
 */
export const EmbeddedPreferredPaymentView = ({ statusOrderPolling }: TemplateViewProps) => {
  const context = useContext(CheckoutContext);
  const {
    viewer: { screen },
    redirectPayment: { redirectUrl, showRedirect },
    successScreen,
    featureFlags,
    template,
    paymentMethodSelected,
    paymentMethodsAvailable,
    error,
  } = context;
  const { errorFollowsContent } = template.features;
  const [walletRevealed, setWalletRevealed] = useState(false);
  const gateOnWallet =
    template.features.deferExpressWalletReveal && paymentMethodSelected === PaymentMethodEnum.APPLE_PAY;
  const showWalletGate = gateOnWallet && !walletRevealed;
  const revealValue = useMemo(
    () => ({ onWalletReady: () => setWalletRevealed(true), isRevealed: !showWalletGate }),
    [showWalletGate]
  );
  const reportWalletAvailability = useExpressWalletFallback();
  useExpressReadyTimeout(
    showWalletGate,
    () => {
      logError('Express wallet gate never lifted; forcing fallback', undefined, {
        template: template.type,
        paymentMethod: paymentMethodSelected,
        timeoutMs: EXPRESS_WALLET_GATE_HANG_TIMEOUT_MS,
      });
      reportWalletAvailability(false, {
        wallet: 'apple_pay',
        provider:
          paymentMethodsAvailable.find((pm) => pm.paymentMethodName === PaymentMethodEnum.APPLE_PAY)?.provider ??
          'unknown',
        reason: EXPRESS_WALLET_GATE_HANG_TIMEOUT_REASON,
        timeoutMs: EXPRESS_WALLET_GATE_HANG_TIMEOUT_MS,
      });
      setWalletRevealed(true);
    },
    EXPRESS_WALLET_GATE_HANG_TIMEOUT_MS
  );
  const { handleUseOtherPaymentMethod } = useOtherPaymentMethodFallback();
  const showPaymentMethodIcons = Boolean(featureFlags?.[FeatureFlag.CHECKOUT_SHOW_PAYMENT_METHOD_ICONS]);
  const useOtherPaymentMethodLink =
    template.features.secondaryUseOtherPaymentMethodLink && !screen ? (
      <UseOtherPaymentMethodLink onClick={handleUseOtherPaymentMethod} showIcons={showPaymentMethodIcons} />
    ) : null;
  const containerRef = useCheckoutDimensionsReporter<HTMLDivElement>();

  return (
    <Modal>
      <ExpressRevealContext.Provider value={revealValue}>
        {showWalletGate && (
          <div data-testid="preferred-payment-wallet-gate" aria-hidden className="absolute inset-0 z-20 bg-white">
            <EmbeddedPreferredSkeletonContent />
          </div>
        )}
        <div
          ref={containerRef}
          data-testid="embedded-preferred-checkout-container"
          className="checkout-embedded-view-font flex h-full w-full flex-col overflow-hidden bg-white"
        >
          <div data-testid="embedded-preferred-summary" className="flex w-full flex-shrink-0 flex-col pt-3">
            <div className="mt-2">
              <TotalSummaryCard />
            </div>
          </div>
          <div className="my-4 w-full flex-shrink-0">
            <Divider />
          </div>
          <div data-testid="preferred-payment-cta" className="flex w-full flex-shrink-0 flex-col gap-5 px-4 pb-5">
            <Payment />
            {useOtherPaymentMethodLink}
          </div>
          <div
            className={cn('w-full flex-shrink-0', errorFollowsContent && 'relative')}
            {...{ [CHECKOUT_CONTENT_END_ATTRIBUTE]: true }}
          >
            {errorFollowsContent && !!error && (
              <div
                data-testid="preferred-payment-error-above-footer"
                className={TEMPLATE_CLASSES.errorOverlayAboveFooter}
              >
                <Error />
              </div>
            )}
            <Footer />
          </div>
          <Card3DSForm />
          <ProcessingLoader />
          {(redirectUrl || showRedirect) && <RedirectPayment />}
          {successScreen?.isOpen && statusOrderPolling === PollingStatus.SUCCESS && <SuccessScreen />}
        </div>
      </ExpressRevealContext.Provider>
    </Modal>
  );
};
